import React, { useEffect, useMemo, useState } from 'react'
import { Button, Grid, Icon, Typography } from 'gu-ui-kit'
import { useTranslation } from 'react-i18next'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/PartnersProgram/partners-program.module.scss'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import RewardCalculator from 'modules/PartnersProgram/subcomponents/RewardCalculator'
import Table from 'modules/PartnersProgram/subcomponents/Table'
import { useAdNetworksTypes } from 'modules/PartnersProgram/subcomponents/RewardCalculator/useAdNetworksTypes'
import { useCalculateAwards } from 'modules/PartnersProgram/subcomponents/RewardCalculator/useCalculateAwards'
import { useDispatch, useSelector } from 'react-redux'
import PageHeader from 'components/PageHeader'
import { PANEL_NAVIGATE } from 'modules/Tickets/types'

const DEFAULT_VALUES = [
  {
    network: 'yandex_direct',
    value: 40000,
  },
  {
    network: 'targeting_ru',
    value: 30000,
  },
  {
    network: 'telegram_ads',
    value: 10000,
  },
]

const PartnersProgram = () => {
  const pathGen = useGeneratePathI18n()
  const { t } = useTranslation()
  const breakpoint = useBreakpoint()
  const dispatch = useDispatch()
  const { getTypes, resetTypes } = useAdNetworksTypes()

  const [values, setValues] = useState(DEFAULT_VALUES)
  const [replenishByPartners, setReplenishByPartners] = useState(true)

  const {
    types_partner_true: { data: types_partner_true = [] } = {},
    types_partner_false: { data: types_partner_false = [] } = {},
    awards: { data: awards, loaded: awardsLoaded } = {},
  } = useSelector((state) => state['partners-program'])

  const { resetAwards, calculate, setAwards } = useCalculateAwards()

  useEffect(() => {
    getTypes({ partner: true })
    getTypes({ partner: false })

    return () => {
      resetTypes()
      resetAwards()
    }
  }, [])

  const notEmptyValues = useMemo(
    () =>
      values.reduce(
        (acc, { network, value = 0 }) =>
          value > 0 ? [...acc, { network, value }] : acc,
        [],
      ),
    [values],
  )

  useEffect(() => {
    if (!notEmptyValues.length) {
      setAwards({ data: {} })
      return
    }

    calculate({
      types: notEmptyValues.map(({ network, value }) => ({
        network: network,
        budget_amount: value,
        partner: replenishByPartners,
      })),
    })
  }, [notEmptyValues, replenishByPartners])

  const types = useMemo(
    () => (replenishByPartners ? types_partner_true : types_partner_false),
    [replenishByPartners, types_partner_true, types_partner_false],
  )

  return (
    <div className={styles['partners-program']}>
      <Grid direction={'column'} gap={64}>
        <Grid direction={'column'} gap={32}>
          <Grid
            gap={16}
            direction={'column'}
            alignItems={'start'}
            justifyContent={'start'}
          >
            <PageHeader color={'Label-Primary'}>
              {t('partners_program::heading')}
            </PageHeader>
            <Typography
              component={'div'}
              variant={
                breakpoint.fbs
                  ? 'Subtitle-25'
                  : breakpoint.lgs
                  ? 'Subtitle-30'
                  : 'Subtitle-40'
              }
              className={styles['partners-program__discounts']}
              color={'Label-Onsurface'}
            >
              {t('partners_program::discounts', { from: 5, to: 25 })}
            </Typography>
          </Grid>
          <Grid direction={'column'} gap={14}>
            <Typography
              component={'h4'}
              variant={'Body-20'}
              color={'Label-Fifth'}
            >
              {t('partners_program::summary')}
            </Typography>
            <Grid
              className={styles['partners-program__list']}
              component={'ul'}
              direction={'column'}
              gap={12}
            >
              {t('partners_program::summary_list', { returnObjects: true }).map(
                (item, index) => (
                  <Typography
                    className={styles['partners-program__list-item']}
                    component={'li'}
                    color={'Label-Fifth'}
                    variant={'Body-15'}
                    key={index}
                  >
                    {item}
                  </Typography>
                ),
              )}
            </Grid>
          </Grid>
          <Grid gap={16}>
            <Button
              fullWidth={breakpoint.sm}
              variant={'primary'}
              size={'l'}
              component={'a'}
              target={'_blank'}
              href={pathGen(ROUTES.PARTNERS_PROGRAM)}
              startIcon={<Icon size={'m'} name={'plus'} />}
            >
              {t('partners_program::more')}
            </Button>
            <Button
              variant={'primary'}
              grey
              size={'l'}
              fullWidth={breakpoint.sm}
              onClick={() =>
                dispatch({
                  type: PANEL_NAVIGATE,
                  payload: {
                    route: 'new_ticket',
                    params: {
                      id: undefined,
                      title: t('partners_program::question'),
                    },
                  },
                })
              }
              startIcon={<Icon size={'m'} name={'user'} />}
            >
              {t('partners_program::consultation')}
            </Button>
          </Grid>
        </Grid>

        <RewardCalculator
          types={types}
          awards={awards}
          setValues={setValues}
          replenishByPartners={replenishByPartners}
          setReplenishByPartners={setReplenishByPartners}
          loaded={awardsLoaded}
          values={values}
        />

        {types && (
          <Table
            data={types}
            setReplenishByPartners={setReplenishByPartners}
            replenishByPartners={replenishByPartners}
          />
        )}
      </Grid>
    </div>
  )
}
export default PartnersProgram
